import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Stack } from '@components/layout';
import { Body } from '@components/text';
import { useDebounce } from '@components/utils/helpers';
import './MetadataContentDesc.scss';

export interface MetadataContentDescProps {
  /** text alignment. default 'left' */
  align?: 'left' | 'center' | 'right';
  /** text content */
  children: React.ReactNode;
  /** additional class names */
  className?: string;
  /** is the text initially collapsed. default false */
  isCollapsed?: boolean;
  /** show or hide the collapse link. default true */
  isCollapsible?: boolean;
  /** number of lines to clamp the text. default 3 */
  lineClamp?: number;
  /** text style of the link. options are link (blue) or text (white), default 'link' */
  linkColor?: 'link' | 'text';
  /** text to display when the text is collapsed, e.g. 'show more' */
  linkTextLess?: string;
  /** text to display when the text is expanded, e.g. 'show less' */
  linkTextMore?: string;
  /** position of the link relative to the text block. default 'bottom' */
  linkPosition?: 'bottom' | 'right';
  /** size of the text. default large */
  size?: 'small' | 'medium' | 'large';
}

export const isTextOverflowed = (element: HTMLElement | null): boolean => {
  return (
    element?.firstChild instanceof HTMLElement &&
    element.firstChild.clientHeight < element.firstChild.scrollHeight
  );
};

/**
 * MetadataContentDesc component - a styled text field with an optional link to show more/less
 * @param align - text alignment. default 'left'
 * @param children - text content
 * @param className - additional classnames
 * @param isCollapsed - whether the text is collapsed by default. default false
 * @param isCollapsible - whether to show or hide the collapse link. default true
 * @param lineClamp - number of lines to clamp the text. default 3
 * @param linkColor - text style of the link. default 'link'
 * @param linkTextLess - text to display when the text is collapsed
 * @param linkTextMore - text to display when the text is expanded
 * @param linkPosition - position of the link. default bottom
 * @param size - size of the text. default large
 */
export const MetadataContentDesc: React.FC<MetadataContentDescProps> = ({
  align = 'left',
  children,
  className,
  isCollapsed = false,
  isCollapsible = true,
  lineClamp = 3,
  linkColor = 'link',
  linkTextLess,
  linkTextMore,
  linkPosition = 'bottom',
  size = 'large',
  ...rest
}) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(isCollapsed);

  const bodyRef = useRef<HTMLSpanElement>(null);

  const checkOverflow = useCallback(() => {
    if (isExpanded) return;
    if (typeof window !== 'undefined' && bodyRef?.current) {
      const { current } = bodyRef;

      const isOverflow = isTextOverflowed(current);
      setHasOverflow(isOverflow);
      if (!isOverflow) setIsExpanded(false);
    }
  }, [isExpanded]);

  const debouncedCheckOverflow = useDebounce(checkOverflow, 200);

  useLayoutEffect(() => {
    checkOverflow();
  }, [checkOverflow]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('resize', debouncedCheckOverflow);
    return () => window.removeEventListener('resize', debouncedCheckOverflow);
  }, [debouncedCheckOverflow]);

  const handleLinkClick = () => {
    setIsExpanded((prevState: boolean) => !prevState);
  };

  const alignFlexMap = {
    center: 'center',
    left: 'flex-start',
    right: 'flex-end',
  };

  const linkAlign = linkPosition === 'right' ? 'flex-end' : alignFlexMap[align];

  return (
    <Stack
      className={classNames('vds-lockup-body', `vds-lockup-body--${size}`, className)}
      flexDirection={linkPosition === 'bottom' ? 'column' : 'row'}
      {...rest}>
      <span ref={bodyRef}>
        <Body
          align={align}
          variant="body-02"
          size={size}
          weight="regular"
          color="primary"
          lineClamp={lineClamp && !isExpanded ? lineClamp : 0}>
          {children}
        </Body>
      </span>
      {linkTextMore && linkTextLess && hasOverflow && isCollapsible && (
        <button
          className={classNames(
            'vds-lockup-body__button',
            `vds-lockup-body__button--${linkColor}`,
            `vds-typography-link-semibold-${size}`,
          )}
          onClick={handleLinkClick}
          aria-hidden="true"
          style={{
            alignSelf: linkAlign,
            flex: 'none',
          }}>
          {isExpanded ? linkTextLess : linkTextMore}
        </button>
      )}
    </Stack>
  );
};
